// per docs/shape-ops-no-report-exceptions.md Decision 6 (T110)
// per docs/caiet-uc-spec.md §UC-ALRT-13; register CF-56
// The flexible exceptions of the missing-report rule, under the excepted locations in
// the rule's condition band: excepted weekdays, excepted dates and excepted Registrar
// types, each a field group with the one-line assistive text the wireframe draws under
// "Locații exceptate" (AdminAlerte.tsx:2054).
import { useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudCheckbox,
  MudChip,
  MudDateInput,
  MudIcon,
  MudInlineMessage,
} from '@egov-moldova/mud-react';
import { formatDate } from '@simsm/ui-shared';
import { REGISTRAR_KINDS, type RegistrarKind } from '@simsm/shared-types';
import {
  ISO_WEEKDAYS,
  everyWeekdayExcepted,
  type NoReportExceptions,
} from './no-report-exceptions';
import './alert-exceptions.css';

function toggled<T>(items: T[], item: T, on: boolean): T[] {
  if (!on) return items.filter((candidate) => candidate !== item);
  return items.includes(item) ? items : [...items, item];
}

export function NoReportExceptionsFields({
  value,
  onChange,
  submitted,
}: {
  value: NoReportExceptions;
  onChange: (value: NoReportExceptions) => void;
  submitted: boolean;
}): JSX.Element {
  const { t } = useTranslate();
  const [dateDraft, setDateDraft] = useState('');
  const [dateIso, setDateIso] = useState<string | null>(null);
  const [dateTried, setDateTried] = useState(false);

  const weekdaysError =
    submitted && everyWeekdayExcepted(value) ? t('alerts.field.except_weekdays_all') : undefined;
  const dateError = dateTried && !dateIso ? t('alerts.field.except_date_invalid') : undefined;

  function addDate(): void {
    setDateTried(true);
    if (!dateIso) return;
    onChange({
      ...value,
      dates: [...new Set([...value.dates, dateIso])].sort((a, b) => a.localeCompare(b)),
    });
    setDateDraft('');
    setDateIso(null);
    setDateTried(false);
  }

  return (
    <>
      <fieldset className="simsm-form-page__fieldset">
        <legend className="simsm-form-page__field-label">
          {t('alerts.field.except_weekdays')}
        </legend>
        <div className="simsm-alerts__check-grid">
          {ISO_WEEKDAYS.map((day) => (
            <MudCheckbox
              key={day}
              size="md"
              checked={value.weekdays.includes(day)}
              invalid={Boolean(weekdaysError)}
              onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                onChange({ ...value, weekdays: toggled(value.weekdays, day, e.detail.checked) })
              }
            >
              <span slot="label">{t(`alerts.weekday.${day}`)}</span>
            </MudCheckbox>
          ))}
        </div>
        {weekdaysError ? (
          <MudInlineMessage size="small" variant="error" hideIcon role="alert">
            {weekdaysError}
          </MudInlineMessage>
        ) : (
          <MudInlineMessage size="small" variant="info" hideIcon>
            {t('alerts.field.except_weekdays_hint')}
          </MudInlineMessage>
        )}
      </fieldset>

      <fieldset className="simsm-form-page__fieldset">
        <legend className="simsm-form-page__field-label">{t('alerts.field.except_dates')}</legend>
        <div className="simsm-alerts__date-editor">
          <MudDateInput
            size="md"
            placeholder={t('common.date.placeholder')}
            aria-label={t('alerts.field.except_dates')}
            value={dateDraft}
            invalid={Boolean(dateError)}
            onMudInput={(e: CustomEvent<{ value: string; isoValue: string | null }>) => {
              setDateDraft(e.detail.value);
              setDateIso(e.detail.isoValue);
            }}
            onMudChange={(e: CustomEvent<{ value: string; isoValue: string | null }>) => {
              setDateDraft(e.detail.value);
              setDateIso(e.detail.isoValue);
            }}
          />
          <MudButton
            shape="circular"
            variant="strict"
            appearance="outlined"
            size="md"
            onClick={addDate}
          >
            <MudIcon slot="icon-start" name="plus-large" size={16} />
            {t('alerts.field.except_date_add')}
          </MudButton>
        </div>
        {dateError ? (
          <MudInlineMessage size="small" variant="error" hideIcon role="alert">
            {dateError}
          </MudInlineMessage>
        ) : (
          <MudInlineMessage size="small" variant="info" hideIcon>
            {t('alerts.field.except_dates_hint')}
          </MudInlineMessage>
        )}
        {value.dates.length > 0 ? (
          <div className="simsm-alerts__label-chips" role="list">
            {value.dates.map((day) => (
              <MudChip
                key={day}
                role="listitem"
                type="input"
                size="sm"
                removable
                onMudRemove={() =>
                  onChange({ ...value, dates: value.dates.filter((item) => item !== day) })
                }
              >
                {formatDate(day)}
              </MudChip>
            ))}
          </div>
        ) : null}
      </fieldset>

      <fieldset className="simsm-form-page__fieldset">
        <legend className="simsm-form-page__field-label">
          {t('alerts.field.except_registrar_kinds')}
        </legend>
        <div className="simsm-alerts__check-grid">
          {REGISTRAR_KINDS.map((kind: RegistrarKind) => (
            <MudCheckbox
              key={kind}
              size="md"
              checked={value.registrarKinds.includes(kind)}
              onMudChange={(e: CustomEvent<{ checked: boolean }>) =>
                onChange({
                  ...value,
                  registrarKinds: toggled(value.registrarKinds, kind, e.detail.checked),
                })
              }
            >
              <span slot="label">{t(`registrars.kind.${kind}`)}</span>
            </MudCheckbox>
          ))}
        </div>
        <MudInlineMessage size="small" variant="info" hideIcon>
          {t('alerts.field.except_registrar_kinds_hint')}
        </MudInlineMessage>
      </fieldset>
    </>
  );
}
